/**
 * Velociraptor Settings types
 *
 * Types for Velociraptor service configuration and management.
 * Used by EvidenX platform administrators.
 */

// =============================================================================
// ENUM TYPES
// =============================================================================

/**
 * Velociraptor deployment type
 */
export type DeploymentType = 'not_configured' | 'local_binary' | 'docker' | 'external_service'

/**
 * Service status
 */
export type ServiceStatus =
  | 'unknown'
  | 'not_installed'
  | 'stopped'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'error'

// =============================================================================
// ENTITY TYPES
// =============================================================================

/**
 * Velociraptor configuration
 */
export interface VelociraptorConfig {
  id: string
  deploymentType: string
  apiHost: string
  apiPort: number
  guiPort: number
  frontendPort: number
  hasCredentials: boolean
  binaryPath: string | null
  datastorePath: string | null
  configFilePath: string | null
  dockerContainerName: string | null
  dockerImage: string | null
  adminUsername: string | null
  serviceStatus: string
  lastHealthCheckAt: string | null
  lastHealthStatus: string | null
  lastErrorMessage: string | null
  installedVersion: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Service status response
 */
export interface ServiceStatusResponse {
  status: string
  isRunning: boolean
  isInstalled: boolean
  version: string | null
  uptimeSeconds: number | null
  lastCheckAt: string
  errorMessage: string | null
}

/**
 * Install result
 */
export interface InstallResult {
  success: boolean
  deploymentType: string
  installedVersion: string | null
  configFilePath: string | null
  apiCredentialsPath: string | null
  errorMessage: string | null
  errorResolution: string | null
}

/**
 * Action response (start/stop)
 */
export interface ActionResponse {
  success: boolean
  status: string
  errorMessage: string | null
}

/**
 * Connectivity test result
 */
export interface ConnectivityTestResult {
  success: boolean
  apiReachable: boolean
  guiReachable: boolean
  latencyMs: number | null
  velociraptorVersion: string | null
  errorMessage: string | null
  errorResolution: string | null
}

/**
 * Test collector result
 */
export interface TestCollectorResult {
  success: boolean
  flowId: string | null
  collectorPath: string | null
  collectorSizeBytes: number | null
  collectorHash: string | null
  targetOs: string
  artefactsIncluded: string[]
  errorMessage: string | null
}

/**
 * Port status
 */
export interface PortStatus {
  port: number
  isAvailable: boolean
  conflictingProcess: string | null
  conflictingPid: number | null
  suggestedAlternative: number | null
}

/**
 * Upgrade result
 */
export interface UpgradeResult {
  success: boolean
  previousVersion: string | null
  newVersion: string | null
  errorMessage: string | null
}

/**
 * Test endpoint
 */
export interface TestEndpoint {
  id: string
  hostname: string
  deviceType: string
  osType: string
  ipAddresses: string[] | null
  systemGuid: string | null
  description: string | null
  isActive: boolean
  createdAt: string
}

// =============================================================================
// CREDENTIAL TYPES
// =============================================================================

/**
 * Credentials for connecting to an external Velociraptor service
 */
export interface ExternalServiceCredentials {
  caCertificate: string
  clientCert: string
  clientKey: string
  apiConnectionString: string
}

// =============================================================================
// INPUT TYPES
// =============================================================================

/**
 * Update configuration input
 */
export interface UpdateConfigInput {
  apiHost?: string
  apiPort?: number
  guiPort?: number
  frontendPort?: number
  binaryPath?: string
  datastorePath?: string
  dockerContainerName?: string
  dockerImage?: string
  adminUsername?: string
  adminPassword?: string
}

/**
 * Install request input
 */
export interface InstallInput {
  deploymentType: string
  adminUsername?: string
  adminPassword?: string
  apiPort?: number
  guiPort?: number
  frontendPort?: number
  binaryPath?: string
  datastorePath?: string
  dockerImage?: string
  apiCredentials?: ExternalServiceCredentials
  credentialsFromEnv?: boolean
}

/**
 * Test collector input
 */
export interface TestCollectorInput {
  targetOs: string
  useQuickTriage?: boolean
}

/**
 * Check ports input
 */
export interface CheckPortsInput {
  ports: number[]
}

// =============================================================================
// DISPLAY CONSTANTS
// =============================================================================

/**
 * Labels for deployment type
 */
export const DEPLOYMENT_TYPE_LABELS: Record<DeploymentType, string> = {
  not_configured: 'Not Configured',
  local_binary: 'Local Binary',
  docker: 'Docker Container',
  external_service: 'External Service',
}

/**
 * Labels for service status
 */
export const SERVICE_STATUS_LABELS: Record<ServiceStatus, string> = {
  unknown: 'Unknown',
  not_installed: 'Not Installed',
  stopped: 'Stopped',
  starting: 'Starting',
  running: 'Running',
  stopping: 'Stopping',
  error: 'Error',
}

/**
 * Colours for service status
 */
export const SERVICE_STATUS_COLOURS: Record<ServiceStatus, string> = {
  unknown: 'gray',
  not_installed: 'gray',
  stopped: 'red',
  starting: 'yellow',
  running: 'green',
  stopping: 'yellow',
  error: 'red',
}

/**
 * Default ports for Velociraptor
 */
export const DEFAULT_PORTS = {
  api: 8001,
  gui: 8889,
  frontend: 8000,
} as const

/**
 * Target OS options for test collector
 */
export const TEST_COLLECTOR_OS_OPTIONS = [
  { value: 'windows', label: 'Windows' },
  { value: 'linux', label: 'Linux' },
  { value: 'darwin', label: 'macOS' },
] as const

/**
 * Deployment type options
 */
export const DEPLOYMENT_TYPE_OPTIONS = [
  { value: 'docker', label: 'Docker Container', description: 'Recommended for most environments' },
  { value: 'local_binary', label: 'Local Binary', description: 'Direct installation on host' },
  {
    value: 'external_service',
    label: 'External Service',
    description: 'Connect to existing Velociraptor instance (cloud/remote)',
  },
] as const
